import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ShieldCheck, FileText, Lock } from 'lucide-react';
import { TERMS_OF_USE, PRIVACY_POLICY, TERMS_LAST_UPDATE } from '../data/termsContent';

const Section = ({ title, body }) => (
  <div className="mb-4">
    <h4 className="text-sm font-semibold text-slate-800 mb-1">{title}</h4>
    <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
  </div>
);

export const TermsGate = () => {
  const { user, needsTermsAcceptance, acceptTerms, logout } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user || !needsTermsAcceptance()) return null;

  const handleAccept = async () => {
    if (!agreed) {
      toast.error('Marque a opção de concordância para continuar.');
      return;
    }
    setSubmitting(true);
    const res = await acceptTerms();
    setSubmitting(false);
    if (res.success) {
      toast.success('Termos aceitos. Bem-vindo(a)!');
    } else {
      toast.error(res.error || 'Não foi possível registrar o aceite.');
    }
  };

  const handleDecline = async () => {
    toast.message('É necessário aceitar os termos para acessar o sistema.');
    await logout();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="terms-gate-overlay"
    >
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-7 py-5 border-b border-slate-100 bg-gradient-to-r from-teal-700 to-teal-600">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Termos de Uso e Política de Privacidade</h2>
              <p className="text-teal-50/90 text-sm">
                Leia e aceite para continuar &middot; Atualizado em {TERMS_LAST_UPDATE}
              </p>
            </div>
          </div>
        </div>

        {/* Conteúdo rolável */}
        <div className="px-7 py-5 overflow-y-auto flex-1" data-testid="terms-gate-content">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-teal-700" />
            <h3 className="text-base font-bold text-slate-900">Termos de Uso</h3>
          </div>
          {TERMS_OF_USE.map((s) => (
            <Section key={s.title} title={s.title} body={s.body} />
          ))}

          <div className="flex items-center gap-2 mt-6 mb-3">
            <Lock className="h-4 w-4 text-teal-700" />
            <h3 className="text-base font-bold text-slate-900">Política de Privacidade (LGPD)</h3>
          </div>
          {PRIVACY_POLICY.map((s) => (
            <Section key={s.title} title={s.title} body={s.body} />
          ))}
        </div>

        {/* Footer / ações */}
        <div className="px-7 py-5 border-t border-slate-100 bg-slate-50">
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(v === true)}
              className="mt-0.5"
              data-testid="terms-agree-checkbox"
            />
            <span className="text-sm text-slate-700">
              Declaro que li e <strong>concordo</strong> com os Termos de Uso e a Política de Privacidade do sistema.
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleDecline}
              disabled={submitting}
              data-testid="terms-decline-button"
            >
              Recusar e sair
            </Button>
            <Button
              type="button"
              onClick={handleAccept}
              disabled={!agreed || submitting}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="terms-accept-button"
            >
              {submitting ? 'Registrando...' : 'Aceitar e continuar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsGate;
